import React, { useCallback, useEffect, useRef, useState } from "react";
import PageSeo from "components/Seo/PageSeo";
import PageHero from "components/PageHero/PageHero";
import CalendarStatus from "components/Calendar/CalendarStatus";
import CalendarLegend from "components/Calendar/CalendarLegend";
import CalendarToolbar from "components/Calendar/CalendarToolbar";
import TermGrid from "components/Calendar/TermGrid";
import MonthView from "components/Calendar/MonthView";
import ListView from "components/Calendar/ListView";
import CalendarDayDetail from "components/Calendar/CalendarDayDetail";
import useCalendarConfig from "hooks/useCalendarConfig";
import "components/Home/homeShared.css";
import "./calendar.css";

const Calendar = () => {
  const {
    config,
    autoDraftDates,
    closedDates,
    closureLabels,
    pdfUrl,
    title,
    year,
    intro,
    subtitle,
    hasTerms,
  } = useCalendarConfig();
  const [activeView, setActiveView] = useState("terms");
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedInfo, setSelectedInfo] = useState(null);
  const detailRef = useRef(null);
  const today = new Date();

  const handleSelectDay = useCallback((date, info) => {
    setSelectedDate(date);
    setSelectedInfo(info);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setSelectedDate(null);
    setSelectedInfo(null);
  }, []);

  useEffect(() => {
    if (!selectedDate || !selectedInfo) return;

    const isMobile = window.matchMedia("(max-width: 899px)").matches;
    if (isMobile) {
      document.body.classList.add("calendar-detail-open");
      return () => document.body.classList.remove("calendar-detail-open");
    }

    detailRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
  }, [selectedDate, selectedInfo]);

  useEffect(() => {
    if (!selectedDate) return undefined;

    const onKeyDown = (event) => {
      if (event.key === "Escape") handleCloseDetail();
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [selectedDate, handleCloseDetail]);

  return (
    <main className="calendar-page">
      <PageSeo
        title={`${title} | Ace Gymnastics`}
        description={
          year
            ? `${year} season calendar for Ace Gymnastics in Bardstown, KY — term dates, closures, and fee auto-draft dates.`
            : "Season calendar for Ace Gymnastics in Bardstown, KY — term dates, closures, and fee auto-draft dates."
        }
        path="/calendar"
      />

      <PageHero
        title={title}
        subtitle={subtitle || undefined}
        imageAlt="Ace Gymnastics season calendar"
      />

      <section
        className="calendar-page__content home-section"
        aria-label="Season calendar"
      >
        <div className="home-section__inner">
          {intro && <p className="calendar-page__intro">{intro}</p>}

          {pdfUrl && (
            <div className="calendar-page__actions">
              <a
                href={pdfUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="site-btn site-btn--accent"
                data-track="calendar_click"
                data-track-location="calendar_page"
                data-track-label="pdf_download"
              >
                Download PDF calendar
              </a>
            </div>
          )}

          {!hasTerms ? (
            <p className="calendar-page__empty">
              The season calendar is being updated. Please check back shortly.
            </p>
          ) : (
            <>
              <CalendarStatus
                config={config}
                closedDates={closedDates}
                today={today}
              />
              <CalendarLegend />
              <CalendarToolbar
                activeView={activeView}
                onViewChange={setActiveView}
              />

              <div
                className={`calendar-page__body${
                  selectedDate ? " calendar-page__body--has-detail" : ""
                }`}
              >
                {activeView === "terms" && (
                  <TermGrid
                    config={config}
                    autoDraftDates={autoDraftDates}
                    closedDates={closedDates}
                    closureLabels={closureLabels}
                    today={today}
                    onSelectDay={handleSelectDay}
                    selectedDate={selectedDate}
                  />
                )}

                {activeView === "month" && (
                  <MonthView
                    config={config}
                    autoDraftDates={autoDraftDates}
                    closedDates={closedDates}
                    closureLabels={closureLabels}
                    today={today}
                    onSelectDay={handleSelectDay}
                    selectedDate={selectedDate}
                  />
                )}

                {activeView === "list" && (
                  <ListView config={config} onSelectDay={handleSelectDay} />
                )}

                {selectedDate && selectedInfo && (
                  <>
                    <button
                      type="button"
                      className="calendar-detail-backdrop"
                      aria-label="Close day details"
                      onClick={handleCloseDetail}
                    />
                    <CalendarDayDetail
                      ref={detailRef}
                      date={selectedDate}
                      info={selectedInfo}
                      onClose={handleCloseDetail}
                    />
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </section>
    </main>
  );
};

export default Calendar;
